<template>
  <div class="ad-controller">
    <el-card class="header-card">
      <template #header>
        <div class="header-section">
          <div class="title-section">
            <el-icon size="24" color="#409EFF"><PictureFilled /></el-icon>
            <span class="page-title">广告管理面板</span>
          </div>
          <div class="action-section">
            <el-button type="primary" @click="handleAdd">
              <el-icon><Plus /></el-icon>
              新增广告
            </el-button>
            <el-button @click="fetchStatistics" :loading="statisticsLoading">
              <el-icon><Refresh /></el-icon>
              刷新统计
            </el-button>
          </div>
        </div>
      </template>

      <!-- 统计卡片 -->
      <div class="statistics-container">
        <div class="stat-card">
          <div class="stat-icon" style="background-color: #ecf5ff;">
            <el-icon size="28" color="#409EFF"><CollectionTag /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-label">广告总数</div>
            <div class="stat-value">{{ statistics.totalCount }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon" style="background-color: #f0f9ff;">
            <el-icon size="28" color="#67C23A"><SuccessFilled /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-label">启用广告</div>
            <div class="stat-value">{{ statistics.enabledCount }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon" style="background-color: #fef0f0;">
            <el-icon size="28" color="#F56C6C"><CircleCloseFilled /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-label">禁用广告</div>
            <div class="stat-value">{{ statistics.disabledCount }}</div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 广告列表 -->
    <el-card class="table-card">
      <template #header>
        <span>广告列表</span>
      </template>

      <!-- 桌面端表格视图 -->
      <el-table 
        v-if="!isMobile"
        :data="advertisements" 
        v-loading="loading"
        style="width: 100%"
        border
      >
        <el-table-column label="序号" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 + (pagination.page - 1) * pagination.size }}
          </template>
        </el-table-column>
        
        <el-table-column label="广告预览" width="150" align="center">
          <template #default="scope">
            <div class="preview-wrapper">
              <el-image
                :src="generateImageUrl(scope.row.url)"
                :preview-src-list="[generateImageUrl(scope.row.url)]"
                fit="cover"
                class="ad-preview-image"
                :preview-teleported="true"
                :z-index="3000"
              >
                <template #error>
                  <div class="image-error">
                    <el-icon><PictureFilled /></el-icon>
                  </div>
                </template>
              </el-image>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="alt" label="广告名称" min-width="150" show-overflow-tooltip />
        
        <el-table-column prop="link" label="跳转链接" min-width="200" show-overflow-tooltip>
          <template #default="scope">
            <el-link :href="scope.row.link" target="_blank" type="primary">
              {{ scope.row.link }}
            </el-link>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="100" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.enable ? 'success' : 'danger'">
              {{ scope.row.enable ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="sort" label="排序" width="80" align="center" />
        
        <el-table-column label="时间范围" min-width="180">
          <template #default="scope">
            <div v-if="scope.row.startTime || scope.row.endTime">
              <div v-if="scope.row.startTime">开始: {{ formatDateTime(scope.row.startTime) }}</div>
              <div v-if="scope.row.endTime">结束: {{ formatDateTime(scope.row.endTime) }}</div>
            </div>
            <span v-else class="text-muted">永久有效</span>
          </template>
        </el-table-column>

        <el-table-column prop="clickCount" label="点击次数" width="100" align="center">
          <template #default="scope">
            <el-tag type="info">{{ scope.row.clickCount || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="创建时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template #default="scope">
            <el-button 
              size="small" 
              type="primary" 
              @click="handleEdit(scope.row)"
              >
              <el-icon><Edit /></el-icon>
              编辑
            </el-button>
            <el-button 
              size="small" 
              type="danger" 
              @click="handleDelete(scope.row)"
              >
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-if="isMobile" v-loading="loading" class="mobile-card-container">
        <div v-if="advertisements.length === 0" class="empty-state">
          <el-empty description="暂无广告数据" />
        </div>
        <div v-else class="ad-cards-grid">
          <div
            v-for="(ad,index) in advertisements"
            :key="ad.id"
            class="ad-card">
            <div class="ad-card-header">
              <div class="ad-card-number">#{{ index + 1 + (pagination.page - 1) * pagination.size }}</div>
              <div class="ad-card-status">
                <el-tag :type="ad.enable ? 'success' : 'danger'" size="small">
                  {{ ad.enable ? '启用' : '禁用' }}
                </el-tag>
              </div>
            </div>
            <div class="ad-card-image">
              <el-image
                :src="generateImageUrl(ad.url)"
                :preview-src-list="[generateImageUrl(ad.url)]"
                fit="cover"
                class="ad-card-image"
                :preview-teleported="true"
                :z-index="3000"
              >
                <template #error>
                  <div class="card-image-error">
                    <el-icon><PictureFilled /></el-icon>
                    <span>图片加载失败</span>
                  </div>
                </template>
              </el-image>
            </div>
            <div class="ad-card-content">
              <div class="ad-card-title"><h4>{{ ad.alt }}</h4></div>
              <div class="ad-card-info">
                <div class="info-item">
                  <span class="info-label">跳转链接:</span>
                  <el-link :href="ad.link" target="_blank" type="primary" class="info-link">
                    {{ad.link.length > 25 ? ad.link.substring(0, 25) + '...' : ad.link}}
                  </el-link>
                </div>
                <div class="info-row">
                  <div class="info-item">
                    <span class="info-label">排序:</span>
                    <span class="info-value">{{ ad.sort }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">点击次数:</span>
                    <el-tag type="info" size="small">{{ ad.clickCount || 0 }}</el-tag>
                  </div>
                  <div class="info-item" v-if="ad.startTime || ad.endTime">
                    <span class="info-label">时间范围：</span>
                    <div class="time-range">
                      <div v-if="ad.startTime" class="time-item">开始: {{ formatDateTime(ad.startTime) }}</div>
                      <div v-if="ad.endTime" class="time-item">结束: {{ formatDateTime(ad.endTime) }}</div>
                    </div>
                  </div>
                  <div class="info-item" v-else>
                    <span class="info-label">时间范围: </span>
                    <span class="info-value text-muted">永久有效</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">创建时间:</span>
                    <span class="info-value">{{ formatDateTime(ad.createdAt) }}</span>
                  </div>
                </div>
              </div>
              <!-- 操作按钮 -->
              <div class="ad-card-actions">
                <el-button
                    type="primary"
                    size="small"
                    @click="handleEdit(ad)"
                    class="action-btn"
                >
                  <el-icon><Edit /></el-icon>
                  编辑
                </el-button>
                <el-button
                    type="danger"
                    size="small"
                    @click="handleDelete(ad)"
                    class="action-btn"
                >
                  <el-icon><Delete /></el-icon>
                  删除
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.size"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
          background
        />
      </div>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-width="100px"
      >
        <el-form-item label="广告名称" prop="alt">
          <el-input 
            v-model="formData.alt" 
            placeholder="请输入广告名称（用于描述）"
            clearable
          />
        </el-form-item>

        <el-form-item label="广告图片" prop="url">
          <div class="upload-section">
            <el-input 
              v-model="formData.url" 
              placeholder="请输入图片URL或上传图片"
              clearable
            >
              <template #append>
                <el-button @click="triggerFileUpload" :loading="uploading">
                  <el-icon><Upload /></el-icon>
                  上传图片
                </el-button>
                <input
                  ref="fileInputRef"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileSelect"
                />
              </template>
            </el-input>
            <div v-if="formData.url" class="preview-container">
              <el-image
                :src="generateImageUrl(formData.url)"
                :preview-src-list="[generateImageUrl(formData.url)]"
                fit="contain"
                class="preview-image"
                :preview-teleported="true"
                :z-index="3000"
              >
                <template #error>
                  <div class="image-error-large">
                    <el-icon><PictureFilled /></el-icon>
                    <div>图片加载失败</div>
                  </div>
                </template>
              </el-image>
            </div>

          </div>
        </el-form-item>

        <el-form-item label="跳转链接" prop="link">
          <el-input 
            v-model="formData.link" 
            placeholder="请输入广告点击后的跳转链接"
            clearable
          />
        </el-form-item>

        <el-form-item label="排序权重" prop="sort">
          <el-input-number 
            v-model="formData.sort" 
            :min="0"
            :max="999"
            controls-position="right"
            placeholder="数值越大越靠前"
          />
          <span class="form-hint">数值越大，广告显示优先级越高</span>
        </el-form-item>

        <el-form-item label="启用状态" prop="enable">
          <el-switch
            v-model="formData.enable"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>

        <el-form-item label="生效时间">
          <el-date-picker
            v-model="timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="YYYY-MM-DD HH:mm:ss"
            clearable
          />
          <div class="form-hint">不设置则永久有效</div>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from 'vue'
import {ElMessage, ElMessageBox} from 'element-plus'
import {
  CircleCloseFilled,
  CollectionTag,
  Delete,
  Edit,
  PictureFilled,
  Plus,
  Refresh,
  SuccessFilled,
  Upload
} from '@element-plus/icons-vue'
import axios from 'axios'

// 响应式数据
const loading = ref(false)
const statisticsLoading = ref(false)
const dialogVisible = ref(false)
const submitting = ref(false)
const formRef = ref(null)
const fileInputRef = ref(null)
const uploading = ref(false)

// 广告列表
const advertisements = ref([])


// 分页数据
const pagination = reactive({
  page: 1,
  size: 10,
  total: 0
})

// 统计数据
const statistics = reactive({
  totalCount: 0,
  enabledCount: 0,
  disabledCount: 0
})

// 表单数据
const formData = reactive({
  id: null,
  url: '',
  alt: '',
  link: '',
  sort: 0,
  enable: true,
  startTime: null,
  endTime: null
})

// 时间范围（用于绑定日期选择器）
const timeRange = ref(null)

// 响应式布局控制
const isMobile = ref(false)

// 检测屏幕尺寸
const checkScreenSize = () => {
  isMobile.value = window.innerWidth <= 768
}

// 监听窗口大小变化
const handleResize = () => {
  checkScreenSize()
}

// 监听时间范围变化
watch(timeRange, (newVal) => {
  if (newVal && newVal.length === 2) {
    formData.startTime = newVal[0]
    formData.endTime = newVal[1]
  } else {
    formData.startTime = null
    formData.endTime = null
  }
})

// 对话框标题
const dialogTitle = computed(() => {
  return formData.id ? '编辑广告' : '新增广告'
})

// 触发文件选择
const triggerFileUpload = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

// 工具函数：生成完整的图片URL
const generateImageUrl = (imagePath) => {
  if (!imagePath) return ''
  
  // 如果已经是完整URL，直接返回
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath
  }
  
  // 确保路径格式正确
  const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath
  
  // 统一使用相对路径：开发环境走 vue devServer 代理，生产环境走 Nginx 反向代理
  return `/static/ad/${cleanPath}`
}

// 工具函数：从完整URL提取相对路径（用于数据库存储）
const extractRelativePath = (fullUrl) => {
  if (!fullUrl) return ''
  
  // 如果已经是相对路径，直接返回
  if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
    return fullUrl
  }
  
  // 从完整URL中提取文件名
  try {
    const url = new URL(fullUrl)
    const pathname = url.pathname
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1)
    return fileName
  } catch (error) {
    console.warn('无法解析URL:', fullUrl)
    return fullUrl
  }
}

// 自定义URL验证器
const validateUrl = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入或上传广告图片'))
    return
  }
  
  // 允许相对路径（以字母、数字或下划线开头的文件名）或完整URL
  const urlPattern = /^(https?:\/\/|\/|[a-zA-Z0-9_-]+\.(jpg|jpeg|png|gif|webp))/i
  if (!urlPattern.test(value)) {
    callback(new Error('请输入有效的图片URL或文件名'))
    return
  }
  
  callback()
}

const validateLink = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入跳转链接'))
    return
  }
  
  // 验证是否为有效的URL
  const urlPattern = /^https?:\/\/.+/i
  if (!urlPattern.test(value)) {
    callback(new Error('请输入有效的URL地址（需要包含 http:// 或 https://）'))
    return
  }
  
  callback()
}

// 表单验证规则
const formRules = {
  alt: [
    { required: true, message: '请输入广告名称', trigger: 'blur' },
    { min: 2, max: 50, message: '广告名称长度在 2 到 50 个字符', trigger: 'blur' }
  ],
  url: [
    { validator: validateUrl, trigger: 'blur' }
  ],
  link: [
    { validator: validateLink, trigger: 'blur' }
  ],
  sort: [
    { required: true, message: '请输入排序权重', trigger: 'blur' },
    { type: 'number', min: 0, max: 999, message: '排序权重必须在 0-999 之间', trigger: 'blur' }
  ]
}

// 获取广告列表
const fetchAdvertisements = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/v1/admin/ads', {
      params: {
        page: pagination.page,
        size: pagination.size
      },
      headers: {
        'Authorization': localStorage.getItem('token')
      }
    })

    if (response.data.code === '200') {
      const data = response.data.data
      advertisements.value = data.data || []
      pagination.total = data.totalRecords || 0
      pagination.page = data.pageNum || 1
      pagination.size = data.pageSize || 10
    } else {
      ElMessage.error(response.data.info || '获取广告列表失败')
    }
  } catch (error) {
    console.error('获取广告列表失败:', error)
    ElMessage.error('获取广告列表失败，请检查网络连接')
  } finally {
    loading.value = false
  }
}

// 获取统计信息
const fetchStatistics = async () => {
  statisticsLoading.value = true
  try {
    const response = await axios.get('/api/v1/admin/ads/statistics', {
      headers: {
        'Authorization': localStorage.getItem('token')
      }
    })

    if (response.data.code === '200') {
      const data = response.data.data
      statistics.totalCount = data.totalCount || 0
      statistics.enabledCount = data.enabledCount || 0
      statistics.disabledCount = data.disabledCount || 0
      ElMessage.success('统计数据已刷新')
    } else {
      ElMessage.error(response.data.info || '获取统计信息失败')
    }
  } catch (error) {
    console.error('获取统计信息失败:', error)
    ElMessage.error('获取统计信息失败')
  } finally {
    statisticsLoading.value = false
  }
}

// 新增广告
const handleAdd = () => {
  resetForm()
  dialogVisible.value = true
}

// 编辑广告
const handleEdit = (row) => {
  formData.id = row.id
  formData.url = row.url
  formData.alt = row.alt
  formData.link = row.link
  formData.sort = row.sort
  formData.enable = row.enable
  formData.startTime = row.startTime
  formData.endTime = row.endTime

  // 设置时间范围
  if (row.startTime && row.endTime) {
    timeRange.value = [row.startTime, row.endTime]
  } else {
    timeRange.value = null
  }

  dialogVisible.value = true
}

// 删除广告
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除广告"${row.alt}"吗？此操作不可恢复。`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    const response = await axios.delete(`/api/v1/admin/ads/${row.id}`, {
      headers: {
        'Authorization': localStorage.getItem('token')
      }
    })

    if (response.data.code === '200') {
      ElMessage.success('删除成功')
      fetchAdvertisements()
      fetchStatistics()
    } else {
      ElMessage.error(response.data.info || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除广告失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

// 提交表单
const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
  } catch (error) {
    return
  }

  submitting.value = true

  try {
    // 准备提交数据，确保URL是相对路径格式
    const submitData = {
      ...formData,
      url: extractRelativePath(formData.url) // 转换为相对路径用于后端存储
    }
    
    console.log('提交数据:', submitData)

    let response
    if (formData.id) {
      // 更新广告
      response = await axios.put(`/api/v1/admin/ads/${formData.id}`, submitData, {
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      })
    } else {
      // 创建广告
      response = await axios.post('/api/v1/admin/ads', submitData, {
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      })
    }

    if (response.data.code === '200') {
      ElMessage.success(formData.id ? '更新成功' : '创建成功')
      dialogVisible.value = false
      fetchAdvertisements()
      fetchStatistics()
    } else {
      ElMessage.error(response.data.info || '操作失败')
    }
  } catch (error) {
    console.error('提交失败:', error)
    ElMessage.error('操作失败，请检查网络连接')
  } finally {
    submitting.value = false
  }
}

// 重置表单
const resetForm = () => {
  formData.id = null
  formData.url = ''
  formData.alt = ''
  formData.link = ''
  formData.sort = 0
  formData.enable = true
  formData.startTime = null
  formData.endTime = null
  timeRange.value = null

  if (formRef.value) {
    formRef.value.clearValidate()
  }
}

// 分页变化
const handleSizeChange = (size) => {
  pagination.size = size
  pagination.page = 1
  fetchAdvertisements()
}

const handlePageChange = (page) => {
  pagination.page = page
  fetchAdvertisements()
}

// 文件选择处理
const handleFileSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  // 验证文件
  const isValid = await validateFile(file)
  if (!isValid) return

  // 上传文件
  await uploadFile(file)
  
  // 清空input，允许重复选择同一文件
  event.target.value = ''
}

// 文件验证
const validateFile = (file) => {
  return new Promise((resolve) => {
    console.log('准备上传文件:', {
      name: file.name,
      type: file.type,
      size: file.size
    })
    
    // 检查文件类型
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      ElMessage.error('只能上传图片文件!')
      resolve(false)
      return
    }
    
    // 检查具体的图片格式
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      ElMessage.error('只支持 JPG、PNG、GIF、WebP 格式的图片!')
      resolve(false)
      return
    }
    
    // 检查文件大小（5MB）
    const isLt5M = file.size / 1024 / 1024 < 5
    if (!isLt5M) {
      ElMessage.error('图片大小不能超过 5MB!')
      resolve(false)
      return
    }
    
    // 检查图片尺寸（可选）
    const img = new Image()
    img.onload = () => {
      const { width, height } = img
      console.log('图片尺寸:', { width, height })
      
      // 可以添加尺寸限制
      if (width > 4000 || height > 4000) {
        ElMessage.warning('图片尺寸较大，建议使用小于4000x4000像素的图片以获得更好的性能')
      }
      
      resolve(true)
    }
    img.onerror = () => {
      ElMessage.error('无法读取图片文件，请选择有效的图片!')
      resolve(false)
    }
    img.src = URL.createObjectURL(file)
  })
}

// 上传文件到服务器
const uploadFile = async (file) => {
  uploading.value = true
  
  try {
    // 创建FormData
    const uploadFormData = new FormData()
    uploadFormData.append('file', file)
    
    console.log('开始上传文件:', file.name)
    
    // 使用axios发送请求
    const response = await axios.post('/api/v1/admin/ads/upload', uploadFormData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': localStorage.getItem('token')
      },
      timeout: 30000 // 30秒超时
    })
    
    console.log('上传响应:', response.data)
    
    if (response.data.code === '200') {
      // 后端返回的是相对路径，直接使用
      formData.url = response.data.data.url
      
      // 显示成功消息
      ElMessage.success('图片上传成功')
      
      // 清除表单验证错误
      if (formRef.value) {
        formRef.value.clearValidate(['url'])
      }
    } else {
      throw new Error(response.data.info || '上传失败')
    }
  } catch (error) {
    console.error('上传错误:', error)
    
    let errorMessage = '图片上传失败，请重试'
    
    // 根据错误类型提供更具体的错误信息
    if (error.response) {
      const status = error.response.status
      if (status === 401) {
        errorMessage = '登录已过期，请重新登录'
      } else if (status === 413) {
        errorMessage = '图片文件过大，请选择小于5MB的图片'
      } else if (status === 415) {
        errorMessage = '不支持的图片格式，请选择jpg、png、gif等格式'
      } else if (error.response.data && error.response.data.info) {
        errorMessage = error.response.data.info
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = '上传超时，请检查网络连接或选择更小的图片'
    } else if (error.message) {
      errorMessage = error.message
    }
    
    ElMessage.error(errorMessage)
  } finally {
    uploading.value = false
  }
}

// 格式化日期时间
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
}

// 页面加载时获取数据
onMounted(() => {
  fetchAdvertisements()
  fetchStatistics()
  checkScreenSize()
  window.addEventListener('resize', handleResize)
})

// 组件卸载时清理事件监听
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.ad-controller {
  padding: 20px;
  background-color: #f5f7fa;
}

.header-card {
  margin-bottom: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.action-section {
  display: flex;
  gap: 10px;
}

/* 统计卡片 */
.statistics-container {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.stat-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
}

/* 表格卡片 */
.table-card {
  margin-bottom: 20px;
}

/* 预览图片容器 */
.preview-wrapper {
  position: relative;
  width: 120px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.preview-wrapper:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.ad-preview-image {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.ad-preview-image:hover {
  transform: scale(1.05);
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  color: #909399;
  font-size: 24px;
  border-radius: 6px;
}

.text-muted {
  color: #909399;
  font-size: 13px;
}

/* 分页 */
.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 表单相关 */
.upload-section {
  width: 100%;
}

.preview-container {
  margin-top: 15px;
  padding: 15px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  background-color: #fafafa;
  transition: all 0.3s ease;
  position: relative;
}

.preview-container:hover {
  border-color: #409EFF;
  background-color: #f0f9ff;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.1);
}

.preview-image {
  width: 100%;
  max-height: 300px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.preview-image:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}



/* 表单提示文字 */
.form-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.image-error-large {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
  font-size: 14px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.image-error-large .el-icon {
  font-size: 48px;
  margin-bottom: 10px;
  opacity: 0.6;
}


/* 移动端卡片布局样式 */
.mobile-card-container {
  min-height: 200px;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.ad-cards-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ad-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
}

.ad-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.ad-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #e9ecef;
}

.ad-card-number {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.ad-card-status {
  display: flex;
  align-items: center;
}

.ad-card-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 0;
  transition: all 0.3s ease;
}

.ad-card-image:hover {
  transform: scale(1.02);
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #6c757d;
  font-size: 14px;
  border: 1px solid #e9ecef;
}

.card-image-error .el-icon {
  font-size: 32px;
  margin-bottom: 8px;
  opacity: 0.6;
}

.ad-card-content {
  padding: 16px;
}

.ad-card-title {
  margin-bottom: 12px;
}

.ad-card-title h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  line-height: 1.4;
  word-break: break-word;
}

.ad-card-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  gap: 4px;
}

.info-row {
  display: flex;
  gap: 16px;
}

.info-row .info-item {
  flex: 1;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.info-value {
  font-size: 13px;
  color: #495057;
  word-break: break-all;
}

.info-link {
  font-size: 13px;
  word-break: break-all;
}

.time-range {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.time-item {
  font-size: 12px;
  color: #495057;
}

.ad-card-actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* Element Plus 图片预览优化 */
:deep(.el-image-viewer__wrapper) {
  z-index: 3000 !important;
}

:deep(.el-image-viewer__mask) {
  background-color: rgba(0, 0, 0, 0.8) !important;
  backdrop-filter: blur(5px) !important;
}

:deep(.el-image-viewer__btn) {
  background-color: rgba(0, 0, 0, 0.6) !important;
  border-radius: 50% !important;
  transition: all 0.3s ease !important;
}

:deep(.el-image-viewer__btn:hover) {
  background-color: rgba(0, 0, 0, 0.8) !important;
  transform: scale(1.1) !important;
}

:deep(.el-image-viewer__canvas) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.el-image-viewer__canvas img) {
  max-width: 90vw !important;
  max-height: 90vh !important;
  border-radius: 8px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .ad-controller {
    padding: 10px;
  }

  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .action-section {
    width: 100%;
  }

  .action-section .el-button {
    flex: 1;
  }

  .statistics-container {
    flex-direction: column;
    gap: 10px;
  }

  .stat-card {
    padding: 15px;
  }

  .stat-value {
    font-size: 24px;
  }

  .preview-wrapper {
    width: 80px;
    height: 60px;
  }

  .ad-preview-image {
    width: 100%;
    height: 100%;
  }

  .image-error {
    width: 100%;
    height: 100%;
    font-size: 20px;
  }

  /* 移动端卡片优化 */
  .ad-cards-grid {
    gap: 12px;
  }

  .ad-card {
    border-radius: 8px;
  }

  .ad-card-image {
    height: 180px;
  }

  .ad-card-content {
    padding: 12px;
  }

  .ad-card-title h4 {
    font-size: 15px;
  }

  .info-row {
    gap: 12px;
  }

  .ad-card-actions {
    padding: 10px 12px;
  }

  .action-btn {
    font-size: 13px;
    padding: 6px 12px;
  }
}

/* 超小屏幕优化 */
@media screen and (max-width: 480px) {
  .ad-controller {
    padding: 8px;
  }

  .ad-cards-grid {
    gap: 10px;
  }

  .ad-card-image {
    height: 160px;
  }

  .ad-card-content {
    padding: 10px;
  }

  .info-row {
    flex-direction: column;
    gap: 6px;
  }

  @media screen and (max-width: 375px){
    .info-item {
      flex-direction: column;
    }
  }
}
</style>
